import React, { useEffect, useMemo, useRef, useState } from "react"

import { useAgentsList } from "@/agents/catalog"
import {
  Button,
  Card,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Textarea,
} from "@coval/ui"
import { ArrowUp, Bot, Paperclip, PencilLine, Square, X } from "lucide-react"

import { isModalLayerTarget } from "@/lib/isKeyboardTypingTarget"
import { areSingleKeyShortcutsEnabled } from "@/lib/singleKeyShortcuts"
import { MotionPauseButton } from "@/components/MotionPauseButton"
import { usePausableMotion } from "@/hooks/usePausableMotion"
import type { TestSetInput } from "@/types/TestSetInput"

import CsvUploadModal, { type CsvUploadTestSetInput } from "../CsvUploadModal"
import { AgentAttachButton } from "./AgentAttachButton"
import AudioUploadModal from "./audio-upload-modal"
import type { TestSetContext } from "./createTestSetContext"
import { FileUploadButton, type AttachedFile } from "./FileUploadButton"
import ScriptTurnsModal from "./ScriptTurnsModal"
import TranscriptModal from "./TranscriptModal"

interface CreateTestSetStep1Props {
  onNext: (context: TestSetContext) => void
  onFileUpload: (
    testSetInput: CsvUploadTestSetInput | TestSetInput,
    variant?: "csv" | "audio" | "script",
  ) => Promise<void>
  initialAgentId?: string
  initialDescription?: string
}

export const CreateTestSetStep1: React.FC<CreateTestSetStep1Props> = ({
  onNext,
  onFileUpload,
  initialAgentId,
  initialDescription,
}) => {
  const [description, setDescription] = useState("")
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([])
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([])
  const [fileError, setFileError] = useState<string | null>(null)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [agentMenuOpen, setAgentMenuOpen] = useState(false)
  const fileUploadRef = useRef<HTMLButtonElement>(null)
  const { data: agents } = useAgentsList()

  const selectedAgents = (agents || []).filter((a) =>
    selectedAgentIds.includes(a.id),
  )

  const handleAgentToggled = (agentId: string) => {
    setSelectedAgentIds((prev) =>
      prev.includes(agentId)
        ? prev.filter((id) => id !== agentId)
        : [...prev, agentId],
    )
  }

  // Pre-populate from URL parameters
  useEffect(() => {
    if (initialAgentId && selectedAgentIds.length === 0) {
      setSelectedAgentIds([initialAgentId])
    }
    if (initialDescription && !description) {
      setDescription(initialDescription)
    }
  }, [initialAgentId, initialDescription, selectedAgentIds.length, description])

  const handleSubmit = () => {
    if (description.trim() && !isSubmitted) {
      setIsSubmitted(true)
      const context: TestSetContext = {
        description: description.trim(),
        files: attachedFiles,
        agents: selectedAgents.length > 0 ? selectedAgents : undefined,
      }
      onNext(context)
    }
  }

  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "0px"
      textareaRef.current.style.height = `${Math.max(40, textareaRef.current.scrollHeight)}px`
    }
  }, [description])

  // Autofocus on mount
  useEffect(() => {
    textareaRef.current?.focus()
  }, [])

  // Focus textarea on any keypress
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const active = document.activeElement as HTMLElement | null
      if (
        active === textareaRef.current ||
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        active?.isContentEditable ||
        active?.closest("[data-radix-popper-content-wrapper]") ||
        isModalLayerTarget(active) ||
        e.metaKey ||
        e.ctrlKey ||
        e.altKey ||
        !areSingleKeyShortcutsEnabled()
      ) {
        return
      }
      if (e.key.length !== 1) {
        return
      }
      textareaRef.current?.focus()
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [])

  const handleFilesSelected = (newFiles: AttachedFile[]) => {
    setFileError(null)
    setAttachedFiles((prev) => [...prev, ...newFiles])
  }

  const totalAttachedSize = useMemo(
    () => attachedFiles.reduce((sum, file) => sum + file.size, 0),
    [attachedFiles],
  )

  const headlines = useMemo(
    () => [
      "Describe what you want to test",
      "Test how your agent handles tricky questions",
      "Generate edge cases for user authentication",
      "Create scenarios for multi-turn conversations",
      "Stress test your agent's error handling",
      "Build test cases from your support tickets",
    ],
    [],
  )
  const [headlineIndex, setHeadlineIndex] = useState(() =>
    Math.floor(Math.random() * headlines.length),
  )

  // The example headlines rotate for as long as the field is empty, so they
  // offer a pause control and start still under a reduced-motion preference.
  const { isStill: isHeadlineStill, toggleStill: toggleHeadline } =
    usePausableMotion()

  useEffect(() => {
    if (isHeadlineStill) {
      return
    }
    const interval = setInterval(() => {
      setHeadlineIndex((index) => (index + 1) % headlines.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [headlines.length, isHeadlineStill])

  const optionCardClass =
    "flex items-center gap-3 w-full h-full px-5 py-5 text-left"

  return (
    <div className="flex items-start justify-center h-full w-full pt-[25vh]">
      <div className="w-full max-w-[680px] xl:max-w-[720px] px-4">
        <div>
          <div className="text-center py-12 px-8 xl:py-16 xl:px-12">
            {/* Description Input Box */}
            <div className="relative mb-2 xl:mb-3">
              {!description && (
                <div
                  aria-hidden
                  className="pointer-events-none absolute left-4 top-4 text-base text-coval-text-muted"
                >
                  {headlines.map((headline, index) => (
                    <span
                      key={headline}
                      className={`absolute left-0 top-0 whitespace-nowrap transition-opacity duration-500 ${
                        index === headlineIndex ? "opacity-100" : "opacity-0"
                      }`}
                    >
                      {headline}
                    </span>
                  ))}
                </div>
              )}
              <Textarea
                ref={textareaRef}
                aria-label="Test description"
                placeholder=""
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onKeyDown={handleKeyPress}
                maxLength={10000}
                rows={1}
                data-testid="test-set-description-input"
                className="min-h-[40px] max-h-[300px] overflow-y-auto caret-coval-text text-base"
                containerClassName="p-4 rounded-lg"
                startAdornment={
                  <>
                    <Button
                      icon
                      variant="ghost"
                      className="min-w-6 w-6 !h-6 p-0 rounded-sm text-coval-text"
                      tooltip="Attach files"
                      onClick={() => fileUploadRef.current?.click()}
                    >
                      <Paperclip className="w-4 h-4" />
                    </Button>
                    <DropdownMenu
                      open={agentMenuOpen}
                      onOpenChange={setAgentMenuOpen}
                    >
                      <DropdownMenuTrigger asChild>
                        <Button
                          icon
                          variant="ghost"
                          className={`min-w-6 w-6 !h-6 p-0 rounded-sm text-coval-text ${agentMenuOpen ? "bg-surface-1" : ""}`}
                          tooltip="Add agents"
                        >
                          <Bot className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        side="bottom"
                        align="start"
                        className="p-0 w-64"
                      >
                        <AgentAttachButton
                          selectedAgentIds={selectedAgentIds}
                          onAgentToggled={handleAgentToggled}
                        />
                      </DropdownMenuContent>
                    </DropdownMenu>
                    {!description && (
                      <MotionPauseButton
                        isStill={isHeadlineStill}
                        onToggle={toggleHeadline}
                        subject="example rotation"
                        className="min-w-6 w-6 !h-6 p-0 rounded-sm text-coval-text"
                      />
                    )}
                    {selectedAgents.length > 0 && (
                      <button
                        type="button"
                        aria-label="Clear selected agents"
                        className="group flex items-center gap-1.5 px-2 h-6 bg-coval-contrast/10 rounded-sm text-xs text-coval-text-muted cursor-pointer"
                        onClick={() => setSelectedAgentIds([])}
                      >
                        <span className="relative w-4 h-4 flex-shrink-0">
                          <Bot className="w-4 h-4 text-coval-contrast absolute inset-0 group-hover:opacity-0 transition-opacity" />
                          <X className="w-4 h-4 text-coval-text-muted absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </span>
                        <span className="truncate max-w-32">
                          {selectedAgents.length === 1
                            ? selectedAgents[0].display_name
                            : `${selectedAgents.length} Agents Selected`}
                        </span>
                      </button>
                    )}
                    {attachedFiles.length > 0 && (
                      <button
                        type="button"
                        aria-label="Clear attached files"
                        className="group flex items-center gap-1.5 px-2 h-6 bg-coval-contrast/10 rounded-sm text-xs text-coval-text-muted cursor-pointer"
                        onClick={() => setAttachedFiles([])}
                      >
                        <span className="relative w-4 h-4 flex-shrink-0">
                          <Paperclip className="w-4 h-4 text-coval-contrast absolute inset-0 group-hover:opacity-0 transition-opacity" />
                          <X className="w-4 h-4 text-coval-text-muted absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </span>
                        <span className="truncate max-w-32">
                          {attachedFiles.length === 1
                            ? attachedFiles[0].name
                            : `${attachedFiles.length} Files`}
                        </span>
                      </button>
                    )}
                  </>
                }
                endAdornment={
                  <Button
                    icon
                    onClick={handleSubmit}
                    disabled={!description.trim() || isSubmitted}
                    className="rounded-full cursor-pointer disabled:opacity-40 disabled:cursor-default bg-coval-contrast text-coval-text-on-contrast"
                    data-testid="test-set-generate-button"
                    tooltip="Generate test set"
                  >
                    {isSubmitted ? (
                      <Square className="w-2.5 h-2.5 fill-current" />
                    ) : (
                      <ArrowUp className="w-4 h-4" />
                    )}
                  </Button>
                }
              />

              <div className="hidden">
                <FileUploadButton
                  onFilesSelected={handleFilesSelected}
                  onValidationError={setFileError}
                  existingFileCount={attachedFiles.length}
                  existingTotalSize={totalAttachedSize}
                  ref={fileUploadRef}
                />
              </div>
            </div>

            {fileError && (
              <p className="text-sm text-coval-danger-text mb-4">{fileError}</p>
            )}

            {/* Options */}
            <p className="text-sm font-semibold text-coval-text-muted text-center mt-6 mb-2.5 opacity-80">
              More ways to create a test set
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 w-full py-2 text-left">
              <div>
                <CsvUploadModal
                  onTestCasesProcessed={(input) => onFileUpload(input, "csv")}
                  variant="text"
                  triggerClassName={optionCardClass}
                />
              </div>
              <div>
                <AudioUploadModal
                  onTestCasesProcessed={(input) => onFileUpload(input, "audio")}
                  triggerClassName={optionCardClass}
                />
              </div>
              <div>
                <ScriptTurnsModal
                  onTestCasesProcessed={(input) =>
                    onFileUpload(input, "script")
                  }
                  triggerClassName={optionCardClass}
                />
              </div>
              <div>
                <TranscriptModal
                  onTestCasesProcessed={(input) =>
                    onFileUpload(input, "script")
                  }
                  triggerClassName={optionCardClass}
                />
              </div>
              <div className="sm:col-span-2">
                <Card
                  interactive
                  className={`${optionCardClass} justify-center`}
                  onClick={() => onNext({ description: "", manual: true })}
                  data-testid="test-set-manual-mode-button"
                >
                  <PencilLine className="w-4 h-4 text-coval-text-muted flex-shrink-0" />
                  Create manually
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
